// Tiny dependency-free inline-SVG sparkline for the Pipeline flow-graph nodes
// (spec §4 / §4.1). No chart library for the mini case — just a normalized
// polyline (+ optional last-point dot) inside a fixed-aspect viewBox so it
// scales crisply at any card size.
//
// Pure presentational, no data fetch. `values` is a short series (≤24 points,
// the trailing-24h hourly buckets); `color` overrides the stroke (e.g. tinted
// by success rate for the jobs sparkline).

export function Sparkline({
  values,
  color = "#34d399", // emerald-400
  width = 96,
  height = 24,
  className = "",
}: {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
  className?: string;
}) {
  // Nothing to plot — render a flat baseline placeholder (no crash, keeps layout).
  if (!values || values.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className={className}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line
          x1={0}
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke="#3f3f46" // zinc-700
          strokeWidth={1}
        />
      </svg>
    );
  }

  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const n = values.length;
  // Pad by 1px top/bottom so the line never clips at the edges.
  const pad = 1;
  const plotH = height - pad * 2;

  const x = (i: number) => (n === 1 ? width / 2 : (i / (n - 1)) * width);
  const y = (v: number) => pad + plotH - ((v - min) / span) * plotH;

  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const lastIdx = n - 1;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={className}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* trailing point marker */}
      <circle cx={x(lastIdx)} cy={y(values[lastIdx])} r={1.6} fill={color} />
    </svg>
  );
}
